import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import parse from 'html-react-parser';
import { Map } from 'immutable';
import { Button, Col, Collapse, Row, Image, Modal } from 'react-bootstrap';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import styles from './style.scss';

const propTypes = {
  event: PropTypes.instanceOf(Map).isRequired,
  settings: PropTypes.instanceOf(Map).isRequired,
  className: PropTypes.string,
};

function FullDescription({ event, settings, description, onResizeComponent }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    onResizeComponent && onResizeComponent(true);
  }, [open]);

  const shouldShowAllText = settings.get('showAllText');
  const characterSize = settings.get('characterSize');
  const trimmedCharacterSize = shouldShowAllText
    ? description
    : description.substring(0, characterSize).trim() + (description.length > characterSize ? '...' : '');

  const moreTextAvailable =
    settings.get('showMoreButton') &&
    settings.get('moreButtonTarget', 'moreText') === 'moreText' &&
    description.length > settings.get('characterSize');

  if (settings.get('showMoreButton') && settings.get('moreButtonTarget') === 'urlLink') {
    return (
      <div>
        <div>{parse(trimmedCharacterSize.replace('&nbs', ''))}</div>
        <a href={event.get('url')} className={cn(styles.moreButton, 'show-more-button')} target="_blank" rel="noreferrer">
          <Button aria-controls="example-collapse-text" className="show-more-button">
            {settings.get('showMoreText')}
          </Button>
        </a>
      </div>
    );
  }

  return (
    <>
      {!open && (
        <div
          dangerouslySetInnerHTML={{
            __html: trimmedCharacterSize,
          }}
        />
      )}
      {moreTextAvailable && !window.isMobileType && (
        <Collapse in={open}>
          <div id="example-collapse-text">
            <div
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          </div>
        </Collapse>
      )}
      {moreTextAvailable && open && window.isMobileType && (
        <ModalCheckout key={event.get('id')} event={event} settings={settings} show={open} setOpen={setOpen}>
          <div
            dangerouslySetInnerHTML={{
              __html: description,
            }}
          />
        </ModalCheckout>
      )}
      {moreTextAvailable && (
        <Button
          className={cn(styles.moreButton, 'show-more-button')}
          aria-controls="example-collapse-text"
          onClick={() => {
            setOpen(!open);
          }}
        >
          {settings.get(!open ? 'showMoreText' : 'showLessText')}
        </Button>
      )}
    </>
  );
}

function Details({ event, settings, className = '', onResizeComponent }) {
  const isEventbrite = event.get('type') === 'eventbrite';
  const useFullDescription = (isEventbrite && settings.get('useFullDescription')) || settings.get('showAllText');
  const showSummary = settings.get('showSummary') && isEventbrite;
  if (!event.get('description')) return <span />;
  if (!settings.get('showAllText') && settings.get('characterSize') === 0) return <span />;
  const enableRtl = settings.get('rightToLeftText');
  const descriptionBase = useFullDescription ? event.get('long_description', '') : event.get('description', '');
  const description = settings.get('forcePlainText') ? descriptionBase.replace(/<[^>]*>/g, '') : descriptionBase;

  const isCustom = event.get('type') === 'eventviewer';
  const details = isCustom ? description : description.replace(/\n/g, '<br />');
  const thumbnailUrls = event.get('description_thumbnail_urls');
  const patchedDetails =
    event.get('type') === 'eventbrite' && useFullDescription && showSummary ? details.replace(event.get('description'), '') : details;
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }} className={`${className} ${enableRtl ? 'rightToLeftText' : ''}`}>
      <div className={cn('description', styles.description, { [styles.detailsContainer]: settings.get('showMoreButton') })}>
        {showSummary && useFullDescription && (
          <div className={styles.summary}>
            <div
              dangerouslySetInnerHTML={{
                __html: event.get('description', ''),
              }}
            />
          </div>
        )}
        <FullDescription event={event} settings={settings} description={patchedDetails} onResizeComponent={onResizeComponent} />
        {thumbnailUrls && <Thumbnails urls={thumbnailUrls} />}
      </div>
    </div>
  );
}
function Thumbnails({ urls }) {
  const isMobile = window.isMobileType;

  const colSize = Math.ceil(12 / Math.min(4, urls.size));

  const props = isMobile ? { xs: 12, sm: 6 } : { xs: 12, sm: colSize, md: colSize };

  return (
    <Row>
      {urls.map((url) => (
        <Col {...props}>
          <Zoom>
            <Image src={url} fluid rounded thumbnail style={{ height: 200, width: '100%', objectFit: 'cover', background: 'transparent' }} />
          </Zoom>
        </Col>
      ))}
    </Row>
  );
}

export const ModalCheckout = ({ event, settings, children, show, setOpen }) => {
  const [modalPosition, setModalPosition] = useState();

  useEffect(() => {
    const containerElement = document.getElementById(`checkout-${event.get('id')}`);
    if (containerElement) {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      const clientRect = containerElement.getBoundingClientRect();
      const top = clientRect.top + scrollY;

      setModalPosition({ top, height: containerElement.clientHeight + 50 });
    } else {
      setModalPosition({ top: 0 });
    }
  }, []);
  if (!modalPosition) return <div />;
  return (
    <Modal show={show} onHide={() => setOpen(false)} fullscreen style={{ height: '80%', position: 'fixed', ...modalPosition }}>
      <Modal.Header closeButton>
        <Modal.Title>
          {settings.get('showMoreText')} for {event.get('title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {children}
        <Button
          className={cn(styles.moreButton, 'show-more-button', styles.flexCenter)}
          onClick={() => {
            setOpen(false);
          }}
        >
          {settings.get('showLessText')}
        </Button>
      </Modal.Body>
    </Modal>
  );
};

Details.propTypes = propTypes;

export default Details;
